/* eslint-disable react/no-unescaped-entities, react/no-unstable-nested-components */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, ArrowRight } from "lucide-react";
import axios from "axios";
import logger from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DestinationsPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [destinations, setDestinations] = useState({ international: [], domestic: [] });
  const [activeTab, setActiveTab] = useState(category || "international");
  const [loading, setLoading] = useState(true);

  const scrollToTop = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await axios.get(`${API}/destinations`);
        setDestinations(response.data);
      } catch (e) {
        logger.error("Error fetching destinations:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, []);

  useEffect(() => {
    if (category) {
      setActiveTab(category);
    }
  }, [category]);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const DestinationCard = ({ destination, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <div
        onClick={() => scrollToTop(`/packages?destination=${destination.name}`)}
        className="cursor-pointer"
        data-testid={`destination-${destination.id}`}
      >
        <div className="card-destination aspect-[4/5] group">
          <img
            src={destination.image}
            alt={destination.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
              <MapPin className="h-4 w-4" />
              {destination.country}
            </div>
            <h3 className="text-2xl font-heading font-semibold text-white mb-2">
              {destination.name}
            </h3>
            <p className="text-white/70 text-sm mb-4">{destination.description}</p>
            <div className="flex items-center justify-between">
              {destination.starting_price > 0 && (
                <div>
                  <span className="text-sm text-white/60">Starting from</span>
                  <div className="text-xl font-bold text-coral">
                    ₹{destination.starting_price?.toLocaleString()}
                  </div>
                </div>
              )}
              <span className="flex items-center gap-2 text-white/60 group-hover:text-coral transition-colors text-sm ml-auto">
                Explore Packages <ArrowRight className="h-4 w-4" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div data-testid="destinations-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900" data-testid="destinations-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=1920&q=80"
            alt="Destinations"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="caption text-coral mb-4 block">Explore</span>
            <h1 className="heading-1 text-white mb-6">Our Destinations</h1>
            <p className="text-lg text-white/80">
              From exotic international getaways to incredible domestic treasures, 
              discover destinations that will take your breath away.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="section-padding bg-white" data-testid="destinations-grid">
        <div className="container-custom">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <motion.div {...fadeInUp} className="flex justify-center mb-12">
              <TabsList className="bg-slate-100 p-1 rounded-full">
                <TabsTrigger
                  value="international"
                  className="px-8 py-3 rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="tab-international"
                >
                  International
                </TabsTrigger>
                <TabsTrigger
                  value="domestic"
                  className="px-8 py-3 rounded-full data-[state=active]:bg-primary data-[state=active]:text-white"
                  data-testid="tab-domestic"
                >
                  Domestic
                </TabsTrigger>
              </TabsList>
            </motion.div>

            <TabsContent value="international">
              <motion.div {...fadeInUp} className="text-center mb-12">
                <h2 className="heading-2 text-primary mb-4">International Destinations</h2>
                <p className="body-lg max-w-2xl mx-auto">
                  Explore the world's most captivating destinations with our expertly curated international packages
                </p>
              </motion.div>
              {loading ? (
                <div className="text-center py-12">Loading destinations...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {destinations.international.map((dest, index) => (
                    <DestinationCard key={dest.id} destination={dest} index={index} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="domestic">
              <motion.div {...fadeInUp} className="text-center mb-12">
                <h2 className="heading-2 text-primary mb-4">Domestic Destinations</h2>
                <p className="body-lg max-w-2xl mx-auto">
                  Discover the incredible beauty of India with our handpicked domestic getaways
                </p>
              </motion.div>
              {loading ? (
                <div className="text-center py-12">Loading destinations...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {destinations.domestic.map((dest, index) => (
                    <DestinationCard key={dest.id} destination={dest} index={index} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-accent" data-testid="destinations-cta">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <h2 className="heading-2 text-primary mb-6">
              Can't Find Your Dream Destination?
            </h2>
            <p className="body-lg mb-8">
              We specialize in customized travel packages. Tell us where you want to go, 
              and we'll create a perfect itinerary just for you.
            </p>
            <Button 
              onClick={() => scrollToTop("/get-quote")}
              className="btn-coral text-base" 
              data-testid="custom-trip-btn"
            >
              Plan a Custom Trip
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DestinationsPage;
